/**
 * @file        src/pages/hotel/SearchLogDetails.jsx
 * @description "View Hotel Search History Details" — one row of hotel_search_logs
 *              (/hotel/search-log/:id). Legacy parity: a two-column "Basic
 *              Details" block and a "Searched By" block (the account behind the
 *              search, or "No Data Found" for a guest).
 * @author      Kinetika Labs
 * @created     2026-07-12
 * @changelog
 *   - 2026-07-12 Claude — Initial (B2C Hotel Management: real search log).
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import TableSkeleton from '../../components/TableSkeleton';
import { get } from '../../api/client';
import { day, dateTime, statusTone } from './cells';

function Row({ label, children }) {
  return (
    <div className="det-row">
      <b>{label} :</b> <span>{children ?? <span className="muted">—</span>}</span>
    </div>
  );
}

export default function SearchLogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['hotel-search-log', 'one', id],
    queryFn: () => get(`/hotel/search-log/${id}`),
  });
  const s = data?.data;

  return (
    <>
      <PageHead
        title="View Hotel Search History Details"
        subtitle="B2C Hotel Management"
        actions={
          <button className="btn ghost" onClick={() => navigate('/hotel/search-log')}>
            <Icon name="x" /> Back to Search Log
          </button>
        }
      />

      {isLoading ? (
        <article className="panel glass"><TableSkeleton /></article>
      ) : isError || !s ? (
        <article className="panel glass"><div className="empty">Could not load this search.</div></article>
      ) : (
        <>
          <article className="panel glass">
            <h3 className="sec-title">Basic Details</h3>
            <div className="det-grid">
              <Row label="ID">{s.id}</Row>
              {/* A guest search is attributed to no account — the legacy console
                  prints 0 rather than leaving the cell blank. */}
              <Row label="Customer ID">{s.user_id || 0}</Row>
              <Row label="Entry Date">{dateTime(s.created_at)}</Row>
              <Row label="Update Date">{dateTime(s.updated_at)}</Row>
              <Row label="Location">{s.location}</Row>
              <Row label="City Code">{s.city_code}</Row>
              <Row label="Check In">{day(s.check_in)}</Row>
              <Row label="Check Out">{day(s.check_out)}</Row>
              <Row label="Country">{s.country}</Row>
              <Row label="Adult">{s.num_adults}</Row>
              <Row label="Room">{s.rooms}</Row>
              <Row label="Child">{s.num_children}</Row>
              <Row label="Trace ID">{s.trace_id}</Row>
              <Row label="Status">{s.status ? <Chip tone={statusTone(s.status)}>{s.status}</Chip> : null}</Row>
              {s.error ? <Row label="Error">{s.error}</Row> : null}
            </div>
          </article>

          <article className="panel glass">
            <h3 className="sec-title">Searched By</h3>
            {s.user_id ? (
              <div className="det-grid">
                <Row label="Customer ID">{s.user_id}</Row>
                <Row label="Name">{s.customer_name}</Row>
                <Row label="Email">{s.customer_email}</Row>
                <Row label="Mobile">{s.customer_phone}</Row>
              </div>
            ) : (
              <div className="empty">No Data Found</div>
            )}
          </article>
        </>
      )}
    </>
  );
}
